import React, { useState, useCallback, useRef } from 'react'
import { Field } from 'react-final-form'
import styles from './FileField.module.css'

type Props = {
  name: string
  multiple?: boolean
  accept?: string
}

export const FileField = ({ name, multiple = true, accept }: Props) => (
  <Field<File[]> name={name}>{({ input }) => <FileInput input={input} multiple={multiple} accept={accept} />}</Field>
)

type FileInputProps = {
  input: {
    name: string
    value: File[] | undefined
    onChange: (value: File[]) => void
  }
  multiple: boolean
  accept?: string
}

const FileInput: React.FC<FileInputProps> = ({ input, multiple, accept }) => {
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Convert FileList → File[]
  const fileListToArray = (files: FileList | null): File[] => (files ? Array.from(files) : [])

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return
      const newFiles = fileListToArray(files)
      const currentFiles = input.value || []

      // Merge without duplicates (by name+size)
      const merged = [
        ...currentFiles,
        ...newFiles.filter((nf) => !currentFiles.some((cf) => cf.name === nf.name && cf.size === nf.size)),
      ]

      input.onChange(merged)
    },
    [input],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragging(false)
      handleFiles(e.dataTransfer.files)
    },
    [handleFiles],
  )

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    e.target.value = '' // reset so same file can be selected again
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  return (
    <div
      className={`${styles.dropZone} ${isDragging ? styles.dragging : ''}`}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        ref={inputRef}
        type='file'
        multiple={multiple}
        accept={accept}
        className={styles.fileInput} // hidden
        onChange={handleChange}
      />
      <p className={styles.instructions}>
        {isDragging ? 'Drop files here...' : 'Drag & drop files here, or click to browse'}
      </p>
      {input.value && input.value.length > 0 && (
        <ul className={styles.fileList}>
          {input.value.map((file, i) => (
            <li key={`${file.name}-${i}`}>{file.name}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
